import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
  TranslateService,
  provideMissingTranslationHandler,
  provideTranslateLoader,
  provideTranslateService,
} from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { DEFAULT_LANGUAGE, EAGER_TRANSLATION_MODULES } from './i18n.config';
import { BaseTranslateLoader } from './translation-loader.service';
import { LanguageService, readStoredLanguage } from './language.service';
import { LazyMissingTranslationHandler } from './lazy-missing-translation.handler';
import { TranslationManagerService } from './translation-manager.service';
import { TranslationRouteService } from './translation-route.service';

/**
 * Єдиний провайдер модульної i18n-системи.
 *
 * ВАЖЛИВО: викликати РІВНО ОДИН РАЗ і тільки в `AppModule.providers`.
 * Якщо це опиниться в `SharedModule` (який імпортують ~24 ліниві модулі),
 * кожен лінивий інжектор отримає власний `TranslateService`/`TranslateStore`,
 * і зміна мови в одному інстансі не дійде до інших — мова перестане
 * перемикатись без перезавантаження сторінки. `BaseTranslateLoader` кричить
 * у консоль, якщо це станеться.
 *
 * Що відбувається на старті застосунку:
 *  1. `TranslateService` конфігурується з `BaseTranslateLoader` — він відповідає
 *     ЛИШЕ за базовий словник `assets/i18n/<lang>.json`;
 *  2. ініціалізатор підписує менеджер на `onLangChange` (щоб зміна мови дотягувала
 *     вже задіяні модулі) і чекає на базовий словник — далі бутстрап;
 *  3. після цього стартують маршрутне автозавантаження та eager-модулі лейауту;
 *  4. в idle фоном докачується решта модулів — як страхувальна сітка.
 */
export function provideI18n(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideTranslateService({
      loader: provideTranslateLoader(BaseTranslateLoader),
      missingTranslationHandler: provideMissingTranslationHandler(LazyMissingTranslationHandler),
      lang: readStoredLanguage(),
      fallbackLang: DEFAULT_LANGUAGE,
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const manager = inject(TranslationManagerService);
      const languageService = inject(LanguageService);
      const routeService = inject(TranslationRouteService);

      const language = readStoredLanguage();

      manager.init();

      return firstValueFrom(translate.use(language))
        .catch(() => null)
        .then(() => {
          languageService.syncFromStorage();
          routeService.start();
          manager.loadModules(EAGER_TRANSLATION_MODULES, language).subscribe();
          manager.prefetchRemaining(language);
        });
    }),
  ]);
}
